import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Search } from 'lucide-react';

const MIN_NATURAL_SIZE = 250;

const ZoomableImage = ({ src, alt, className = '', containerClassName = '', showHint = true }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [canZoom, setCanZoom] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const handler = (e) => setPrefersReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const checkImageZoomable = useCallback(() => {
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      if (naturalWidth > 0 && naturalHeight > 0) {
        setCanZoom(Math.max(naturalWidth, naturalHeight) >= MIN_NATURAL_SIZE);
      }
    }
  }, []);

  const handleImageLoad = useCallback(() => {
    checkImageZoomable();
  }, [checkImageZoomable]);

  // Reset state when image source changes
  useEffect(() => {
    setIsZoomed(false);
    setCanZoom(false);
    setHasHovered(false);
    // Handle already-cached images where onLoad won't fire
    const timer = requestAnimationFrame(() => {
      if (imgRef.current && imgRef.current.complete) {
        checkImageZoomable();
      }
    });
    return () => cancelAnimationFrame(timer);
  }, [src, checkImageZoomable]);

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current || !canZoom) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x, y });
  }, [canZoom]);

  const handleMouseEnter = useCallback(() => {
    if (canZoom && !isTouchDevice) {
      setIsZoomed(true);
      setHasHovered(true);
    }
  }, [canZoom, isTouchDevice]);

  const handleMouseLeave = useCallback(() => {
    setIsZoomed(false);
  }, []);

  // Mobile/touch: render plain image
  if (isTouchDevice) {
    return (
      <div className={containerClassName}>
        <img src={src} alt={alt} className={className} />
      </div>
    );
  }

  const transitionDuration = prefersReducedMotion ? '0ms' : '200ms';

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden ${containerClassName}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: canZoom ? (isZoomed ? 'zoom-out' : 'zoom-in') : 'default' }}
      data-testid="zoomable-image-container"
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        className={className}
        style={{
          transform: isZoomed ? 'scale(2)' : 'scale(1)',
          transformOrigin: `${origin.x}% ${origin.y}%`,
          transition: `transform ${transitionDuration} ease-out`,
          willChange: isZoomed ? 'transform' : 'auto',
        }}
        draggable={false}
      />

      {/* "Hover to zoom" hint — visible before first hover, hidden after */}
      {showHint && canZoom && !hasHovered && (
        <div
          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium pointer-events-none select-none animate-fade-in"
          data-testid="zoom-hint"
        >
          <Search className="w-3 h-3" />
          Hover to zoom
        </div>
      )}
    </div>
  );
};

export default ZoomableImage;
